import axios from "axios";
import { logoutUser, refreshAccessToken } from "../api/config";
import { toast } from "react-toastify"; // replace with your actual alert/toast lib

// FASTAPI BASE URL
export const FAST_API_URL = process.env.REACT_APP_PYTHON_FAST_API_URL;

// FASTAPI AXIOS INSTANCE
export const fastApiAuthAxios = axios.create({
  baseURL: FAST_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// FASTAPI PUBLIC ENDPOINTS (no auth header, no logout-on-401 handling)
const FAST_API_PUBLIC_ENDPOINTS = [];

// Check whether endpoint is public
const isPublicEndpoint = (url = "") =>
  FAST_API_PUBLIC_ENDPOINTS.some(
    (endpoint) => url === endpoint || url.startsWith(endpoint + "/")
  );

// Friendly, user-facing copy for rate-limit (429) responses.
export const FAST_API_RATE_LIMIT_MESSAGE =
  "You're doing that a bit too fast. Please wait a few seconds and try again.";

// Throttle rate-limit toasts so a burst of parallel 429s shows only one alert.
let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_GAP_MS = 4000;

const notifyRateLimited = (error) => {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_GAP_MS) return;
  lastRateLimitToastAt = now;

  const retryAfter = Number(error?.response?.headers?.["retry-after"]);
  const message =
    Number.isFinite(retryAfter) && retryAfter > 0
      ? `You're doing that a bit too fast. Please wait ${retryAfter} second${
          retryAfter === 1 ? "" : "s"
        } and try again.`
      : FAST_API_RATE_LIMIT_MESSAGE;

  toast.error(message, { toastId: "fast-api-rate-limit" });
};

// FASTAPI REQUEST INTERCEPTOR
fastApiAuthAxios.interceptors.request.use(
  (config) => {
    if (isPublicEndpoint(config.url)) {
      return config;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      logoutUser();
      throw new axios.Cancel("Session expired");
    }

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// FASTAPI RESPONSE INTERCEPTOR
fastApiAuthAxios.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const errorCode = error?.response?.data?.error_code;
    const message = error?.response?.data?.message;

    // Network error / no response at all — nothing to branch on, just reject
    if (!error?.response) {
      return Promise.reject(error);
    }

    // Skip public endpoints entirely
    if (isPublicEndpoint(originalRequest?.url)) {
      return Promise.reject(error);
    }

    // Case 1: token expired -> attempt one silent refresh, then retry original request
    if (
      status === 401 &&
      errorCode === "TOKEN_EXPIRED" &&
      !originalRequest?._retry
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return fastApiAuthAxios(originalRequest);
        }
      } catch (refreshError) {
        console.error("FastAPI token refresh failed:", refreshError);
      }

      // Refresh failed or returned no token -> force logout
      logoutUser();
      return Promise.reject(error);
    }

    // Case 2: unauthenticated (missing/invalid token, or retry already failed) -> logout
    if (status === 401) {
      logoutUser();
      return Promise.reject(error);
    }

    // Case 3: authenticated but not permitted (e.g. admin hitting a superadmin-only route)
    // -> show an alert, do NOT log out
    if (status === 403) {
      toast.error(message || "You do not have permission to perform this action");
      return Promise.reject(error);
    }

    // Case 4: rate limited (IP-based or user-based) -> show one friendly, throttled
    // alert and let the caller handle the rejection as usual
    if (status === 429 || errorCode === "RATE_LIMIT_EXCEEDED") {
      notifyRateLimited(error);
      return Promise.reject(error);
    }

    // Everything else (500, 503 MSSQL_ERROR, validation errors, etc.) -> let caller handle it
    return Promise.reject(error);
  }
);